import AgoraDatePicker from '@components/V3/Utils/InputsV3/AgoraDatePicker';
import Input from '@components/V4/Inputs/Input';
import Select from '@components/V4/Select/Select';
import { zodResolver } from '@hookform/resolvers/zod';
import SessionModalFormWrapper from '@modules/Admin/components/DetailsModal/DetailsModalFormWrapper';
import { subjectsOptions } from '@shared/constants';
import { useCreateEnrollment, useGetAllStudents } from '@actions';
import { GetAllCoursesLiteDTO } from '@shared/clientTypes/DTOs/CoursesDTO';
import useToast from 'apps/agora/src/hooks/useToast';
import { BasicProps } from 'apps/agora/src/utils/types';
import moment from 'moment';
import { useContext, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MentorDetailsPageContext } from '../../../MentorDetailsPage';
import { useDebounce } from '@hooks';
import TextArea from '@components/V4/Inputs/Textarea';

interface EnrollmentFormProps extends BasicProps {
  courses?: GetAllCoursesLiteDTO[];
  onCloseEditForm: () => void;
}

const enrollmentSchema = z.object({
  course: z.string().min(1, { message: 'Please select a course' }),
  subject: z.string({ message: 'Please select a subject' }),
  student: z.object({
    _id: z.string({ message: 'Please select a student' }),
    fullName: z.string(),
  }),
  noOfMeetings: z.number({ message: 'Please enter a valid number' }),
  startDate: z.string({ message: 'Please select a start date' }),
  endDate: z.string({ message: 'Please select an end date' }),
  comment: z.string().optional(),
});

type FormData = z.infer<typeof enrollmentSchema>;

const TeachingEnrollmentsForm = (props: EnrollmentFormProps) => {
  const { courses, onCloseEditForm } = props;

  const { mentor } = useContext(MentorDetailsPageContext);

  const mentorId = mentor?._id ?? '';

  const [studentSearch, setStudentSearch] = useState('');

  const debouncedStudentSearch = useDebounce(studentSearch, 500);

  const { data: students } = useGetAllStudents({
    active: true,
    search: debouncedStudentSearch,
    limit: 20,
  });

  const toast = useToast();

  const { mutate: createEnrollment, isLoading: creatingEnrollment } = useCreateEnrollment({
    onSuccess: () => {
      toast.success('Enrollment added successfully.');
      onCloseEditForm();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message);
    },
  });

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    control,
    getValues,
    formState: { errors, touchedFields },
  } = useForm<FormData>({
    resolver: zodResolver(enrollmentSchema),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
  });

  const { field: courseField, fieldState: courseFieldState } = useController({
    name: 'course',
    control,
  });

  const { field: subjectField, fieldState: subjectFieldState } = useController({
    name: 'subject',
    control,
  });

  const { field: studentField, fieldState: studentFieldState } = useController({
    name: 'student',
    control,
  });

  const { field: startDateField, fieldState: startDateFieldState } = useController({
    name: 'startDate',
    control,
  });

  const { field: endDateField, fieldState: endDateFieldState } = useController({
    name: 'endDate',
    control,
  });

  const touchAllFields = (
    fields: Partial<Record<keyof FormData, string | number | { _id: string; fullName: string }>>
  ) => {
    Object.keys(fields).forEach((key) => {
      setValue(key as keyof FormData, getValues()[key as keyof FormData], {
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  const onSubmit = async (data: FormData) => {
    const isValid = await trigger();

    if (isValid && mentorId) {
      createEnrollment({
        userId: data.student._id,
        courseId: data.course,
        subject: data.subject,
        mentorId: mentorId,
        startDate: data.startDate,
        estimatedEndDate: data.endDate,
        numberOfSessions: data.noOfMeetings,
        initialComment: data.comment,
      });
    }
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    const formData = getValues();

    touchAllFields(formData);

    handleSubmit(onSubmit)(e);
  };

  return (
    <SessionModalFormWrapper
      title={'New Enrollment'}
      submitButtonText="Create Enrollment"
      isLoading={creatingEnrollment}
      onCancel={onCloseEditForm}
      submitHandler={submitHandler}
    >
      <div className="flex justify-between gap-4">
        <Select
          size="large"
          isRequired
          label="Course"
          options={
            courses?.map((course) => ({
              label: course.title,
              value: course._id,
            })) ?? []
          }
          value={courseField.value}
          onSelect={(value) => courseField.onChange(value)}
          onClear={() => courseField.onChange(undefined)}
          onBlur={courseField.onBlur}
          isValid={!courseFieldState.error}
          isTouched={courseFieldState.isTouched}
          errorText={courseFieldState.error?.message}
        />
        <Select
          size="large"
          isRequired
          label="Subject"
          options={subjectsOptions.map((subject) => ({
            label: subject,
            value: subject,
          }))}
          value={subjectField.value}
          onSelect={(value) => subjectField.onChange(value)}
          onClear={() => subjectField.onChange(undefined)}
          onBlur={subjectField.onBlur}
          isValid={!subjectFieldState.error}
          isTouched={subjectFieldState.isTouched}
          errorText={subjectFieldState.error?.message}
        />
      </div>
      <div className="flex justify-between gap-4">
        <Select
          size="large"
          label="Student"
          onSearch={setStudentSearch}
          filterOption={() => true}
          options={
            students?.map((student) => ({
              label: student?.fullName,
              value: student?._id,
            })) ?? []
          }
          value={studentField.value?._id}
          onSelect={(value) =>
            studentField.onChange(students?.find((student) => student._id === value))
          }
          onClear={() => studentField.onChange(undefined)}
          onBlur={studentField.onBlur}
          isValid={!studentFieldState.error}
          isTouched={studentFieldState.isTouched}
          errorText={studentFieldState.error?.message}
        />
        <Input
          isRequired
          type="number"
          id="noOfMeetings"
          label="No. of meetings"
          isTouched={touchedFields.noOfMeetings}
          isValid={!errors.noOfMeetings}
          errorText={errors.noOfMeetings?.message}
          {...register('noOfMeetings', { required: true, valueAsNumber: true })}
        />
      </div>
      <div className="flex justify-between gap-4">
        <AgoraDatePicker
          isRequired
          label="Start Date"
          allowClear={false}
          value={startDateField.value ? moment(startDateField.value) : undefined}
          onChange={(value) => startDateField.onChange(moment(value).format('YYYY-MM-DD'))}
          onBlur={startDateField.onBlur}
          isValid={!startDateFieldState.error}
          isTouched={startDateFieldState.isTouched}
          errorText={startDateFieldState.error?.message}
        />
        <AgoraDatePicker
          isRequired
          label="End Date"
          allowClear={false}
          value={endDateField.value ? moment(endDateField.value) : undefined}
          onChange={(value) => endDateField.onChange(moment(value).format('YYYY-MM-DD'))}
          onBlur={endDateField.onBlur}
          isValid={!endDateFieldState.error}
          isTouched={endDateFieldState.isTouched}
          errorText={endDateFieldState.error?.message}
        />
      </div>
      <div className="flex justify-between gap-4">
        <TextArea id="comment" label="Comment" className="h-40" {...register('comment')} />
      </div>
    </SessionModalFormWrapper>
  );
};

export default TeachingEnrollmentsForm;
